/**
 * Everything WorkWise needs inside the standard 5-step New Deployment wizard:
 * one react-hook-form for the WorkWise fields of steps 3-5, the lookups
 * (availability, VM names, versions, admin sizes, size prices, shared servers),
 * per-step validation, and the create call that replaces the generic create in
 * executeDeployment.
 *
 * Environment, region, CI/CD and the step-2 size are owned by the standard
 * steps; they are copied into the WorkWise form so its validation (VM-name
 * letters, shared-server filters) always matches what the page shows.
 *
 * Revision 7: the step-2 prices come from GET /workwise/size-prices/ and the
 * estimate from POST /workwise/cost-estimate/; the create payload carries the
 * estimate's total (0 when it is unavailable).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { type FieldPath, useForm, useWatch } from 'react-hook-form'
import { useQueryClient } from '@tanstack/react-query'
import { WORKWISE_SKUS, isWorkwiseEnvironment } from '@/config/workwise'
import { workwiseApi } from '@/services/workwise'
import { useAuthStore } from '@/stores/authStore'
import { useDeploymentWizard } from '@/stores/deploymentWizard'
import { useWorkwiseDraft } from '@/stores/workwiseDraft'
import type { CostEstimate, WorkwiseCicd, WorkwiseFormValues, WorkwiseSku } from '@/types/workwise'
import { parseApiError } from '@/utils/apiError'
import { type AdminSizes, maxUsersOf, readAdminSizes } from '@/utils/workwise/adminSizes'
import type { SizeInfo } from '@/utils/workwise/cost'
import { estimatedMonthlyCostOf } from '@/utils/workwise/costEstimate'
import { DB_STORAGE_PANEL_PATH } from '@/utils/workwise/dbStorage'
import { defaultFormValues } from '@/utils/workwise/defaults'
import { buildPayload, validatePayload } from '@/utils/workwise/payload'
import { validateForm, workwiseResolver } from '@/utils/workwise/resolver'
import { type MappedServerErrors, REVIEW_STEP, mapServerErrors, stepForPath } from '@/utils/workwise/serverErrors'
import { deploymentKeys } from './useDeployments'
import { useDebouncedValue } from './useDebouncedValue'
import { useSizeConfigs } from './useProducts'
import {
  costEstimateKey,
  costEstimateQuery,
  useCustomerAvailability,
  useExistingNetworks,
  useSizePrices,
  useVmNames,
  useWorkwiseVersions,
  workwiseKeys,
} from './useWorkwise'
import { useWorkwiseAutoFields } from './useWorkwiseAutoFields'

/**
 * Fields validated when leaving step 3 and step 4 (the production confirmation lives on step 5). While the
 * application step is skipped the schema leaves `app` out, so validating step 4 passes.
 */
const STEP_FIELDS: Record<3 | 4, FieldPath<WorkwiseFormValues>[]> = {
  3: ['customer.name', 'customer.number', 'customer.slug', 'deploymentModel', 'appServer', 'dbServer', 'gatewayServer', 'network', 'tags'],
  4: ['app'],
}

export interface WorkwiseFlowInput {
  /** True when WorkWise is the selected product. */
  enabled: boolean
  /** Values of the standard steps. */
  environment: string
  region: string
  cicd: string
  appSku?: string
  /** Id of the `workwise` product (for its admin sizes). */
  productId?: number
}

const asSku = (value: string | undefined): WorkwiseSku | undefined =>
  value && (WORKWISE_SKUS as string[]).includes(value) ? (value as WorkwiseSku) : undefined
const asCicd = (value: string): WorkwiseCicd | undefined =>
  value === 'github_actions' || value === 'azure_devops' ? value : undefined

function focusFirstInvalid(): void {
  requestAnimationFrame(() => {
    document.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
  })
}

function sizeInfoFor(adminSizes: AdminSizes): SizeInfo {
  return { vmSizeFor: (sku) => adminSizes[sku]?.vmSize || null }
}

/** How long Deploy waits for an estimate that is not cached yet before sending 0. */
const ESTIMATE_WAIT_MS = 8_000

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timed out')), ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (error: unknown) => {
        clearTimeout(timer)
        reject(error)
      },
    )
  })
}

/** The production confirmation is typed on the Review step, not on step 3. */
function stepOfProblem(path: string): number {
  return path === 'customer.confirmNumber' ? REVIEW_STEP : stepForPath(path)
}

export function useWorkwiseFlow(input: WorkwiseFlowInput) {
  const { enabled } = input
  const queryClient = useQueryClient()
  const owner = useAuthStore((s) => s.user?.email) ?? ''
  const appSku = asSku(input.appSku)

  const [defaults] = useState(() =>
    defaultFormValues({
      owner,
      environment: isWorkwiseEnvironment(input.environment) ? input.environment : undefined,
      region: input.region || undefined,
      cicd: asCicd(input.cicd),
      appSku,
    }),
  )
  const form = useForm<WorkwiseFormValues>({ resolver: workwiseResolver, mode: 'onTouched', defaultValues: defaults })

  // Copy the values owned by the standard steps into the WorkWise form.
  useEffect(() => {
    if (isWorkwiseEnvironment(input.environment) && form.getValues('environment') !== input.environment) {
      form.setValue('environment', input.environment)
    }
  }, [form, input.environment])
  useEffect(() => {
    if (input.region && form.getValues('region') !== input.region) form.setValue('region', input.region)
  }, [form, input.region])
  useEffect(() => {
    const cicd = asCicd(input.cicd)
    if (cicd && form.getValues('cicd') !== cicd) form.setValue('cicd', cicd)
  }, [form, input.cicd])
  useEffect(() => {
    if (appSku && form.getValues('appServer.sku') !== appSku) form.setValue('appServer.sku', appSku)
  }, [form, appSku])

  const [environment, number] = useWatch({ control: form.control, name: ['environment', 'customer.number'] })
  // The Application (ERP) step is skipped (infrastructure only): the page jumps from step 3 to Review and back.
  const skipApplication = useWatch({ control: form.control, name: 'skipApplication' }) === true
  const setSkipApplication = useCallback(
    (skip: boolean) => form.setValue('skipApplication', skip, { shouldDirty: true }),
    [form],
  )

  // R4: an existing network chosen for another region or environment no longer applies. Kept here,
  // not in the Network section, so it also holds while that section is not shown (Dedicated model).
  const [region, existingNetworkId] = useWatch({ control: form.control, name: ['region', 'network.existingNetworkId'] })
  const existingNetworks = useExistingNetworks(region, environment, enabled).data
  useEffect(() => {
    if (existingNetworks && existingNetworkId != null && !existingNetworks.some((n) => n.id === existingNetworkId)) {
      form.setValue('network.existingNetworkId', undefined)
    }
  }, [form, existingNetworks, existingNetworkId])

  const debouncedNumber = useDebouncedValue(number, 400)
  const availability = useCustomerAvailability(number, environment, enabled)
  const vmNames = useVmNames(environment, debouncedNumber, enabled).data
  const versions = useWorkwiseVersions(enabled)
  const defaultVersion = versions.data?.find((v) => v.is_default)?.version ?? versions.data?.[0]?.version

  const sizeConfigs = useSizeConfigs().data
  const adminSizes = useMemo(() => readAdminSizes(sizeConfigs, input.productId), [sizeConfigs, input.productId])
  const sizeInfo: SizeInfo = useMemo(() => sizeInfoFor(adminSizes), [adminSizes])
  // R7-B: VM compute per size for the region shown by the wizard (step 2 shows the default region's).
  const sizePricesQuery = useSizePrices(input.region, enabled)
  const sizePrices = sizePricesQuery.data
  const sizePricesStatus: 'loading' | 'ready' | 'error' = sizePricesQuery.isError
    ? 'error'
    : sizePricesQuery.data
      ? 'ready'
      : 'loading'

  const auto = useWorkwiseAutoFields(form, {
    appNext: vmNames?.app.next,
    dbNext: vmNames?.db.next,
    gatewayNext: vmNames?.gateway.next,
    adminOsType: appSku ? (adminSizes[appSku]?.osType ?? null) : null,
    defaultVersion,
    owner,
  })

  const [serverErrors, setServerErrors] = useState<MappedServerErrors | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  /** Invalid fields re-validate while typing, so a fixed error clears before the next click. */
  const touchInvalid = useCallback(() => {
    // The DB storage panel's total is not an input (the panel re-checks itself), so there is nothing to touch.
    const paths = Object.keys(validateForm(form.getValues())).filter((path) => path !== DB_STORAGE_PANEL_PATH)
    for (const path of paths as FieldPath<WorkwiseFormValues>[]) {
      form.setValue(path, form.getValues(path), { shouldTouch: true })
    }
  }, [form])

  /** Validates the WorkWise fields of step 3 or 4 before the page moves on. */
  const validateStep = useCallback(
    async (step: 3 | 4): Promise<boolean> => {
      const ok = await form.trigger(STEP_FIELDS[step])
      if (step === 3 && availability.status === 'taken') {
        form.setError('customer.number', { type: 'server', message: availability.message })
        focusFirstInvalid()
        return false
      }
      if (!ok) {
        touchInvalid()
        focusFirstInvalid()
      }
      return ok
    },
    [form, availability, touchInvalid],
  )

  /** Validates everything before the Deploy modals open; jumps to the first step with a problem. */
  const validateAll = useCallback(async (): Promise<boolean> => {
    await form.trigger()
    const problems = Object.keys(validateForm(form.getValues()))
    if (problems.length === 0) return true
    touchInvalid()
    const step = Math.min(...problems.map(stepOfProblem))
    if (step < REVIEW_STEP) useDeploymentWizard.getState().setStep(step)
    focusFirstInvalid()
    return false
  }, [form, touchInvalid])

  /**
   * R7-C: the payload's estimatedMonthlyCost = the estimate's total_monthly for these values. The
   * estimate shown on Review is normally cached; otherwise it is requested once, and 0 is sent when
   * it is unavailable (an endpoint failure never blocks the deployment).
   */
  const estimatedMonthlyCost = useCallback(
    async (values: WorkwiseFormValues): Promise<number> => {
      const key = costEstimateKey(values)
      if (!key) return 0
      const query = costEstimateQuery(key)
      const cached = queryClient.getQueryData<CostEstimate>(query.queryKey)
      if (cached) return estimatedMonthlyCostOf(cached)
      try {
        return estimatedMonthlyCostOf(await withTimeout(queryClient.fetchQuery(query), ESTIMATE_WAIT_MS))
      } catch {
        return 0
      }
    },
    [queryClient],
  )

  const applyServerErrors = useCallback(
    (mapped: MappedServerErrors) => {
      setServerErrors(mapped)
      setSubmitError('The server rejected some of the values. Nothing was created; fix them and deploy again.')
      mapped.fields.forEach((f) => form.setError(f.path as FieldPath<WorkwiseFormValues>, { type: 'server', message: f.message }))
      if (mapped.firstStep !== null && mapped.firstStep < REVIEW_STEP) useDeploymentWizard.getState().setStep(mapped.firstStep)
      focusFirstInvalid()
    },
    [form],
  )

  /**
   * POST /workwise/deployments/. Replaces only the generic create call in
   * executeDeployment; the page then continues with its ticket / trigger /
   * navigate logic. If a deployment was already created (its trigger failed),
   * the same id is returned and nothing is sent again.
   */
  const create = useCallback(async (approval?: { required: boolean; notes: string }): Promise<{ id: number }> => {
    const existing = useWorkwiseDraft.getState().created
    if (existing) return { id: existing.id }
    setSubmitError(null)
    setServerErrors(null)
    setCreating(true)
    try {
      const values = form.getValues()
      const payload = buildPayload({
        values,
        correlationId: useWorkwiseDraft.getState().ensureCorrelationId(),
        estimatedMonthlyCost: await estimatedMonthlyCost(values),
        approval,
      })
      const problems = validatePayload(payload)
      if (problems) {
        applyServerErrors(mapServerErrors(problems, [], { dbStorage: values.dbServer.storage }))
        throw new Error('The WorkWise request is not valid.')
      }
      try {
        const res = await workwiseApi.createDeployment(payload)
        useWorkwiseDraft.getState().setCreated({ id: res.data.id, correlationId: payload.correlationId })
        void queryClient.invalidateQueries({ queryKey: deploymentKeys.lists() })
        void queryClient.invalidateQueries({ queryKey: workwiseKeys.all })
        return { id: res.data.id }
      } catch (error) {
        const parsed = parseApiError(error)
        if (parsed.kind === 'validation') {
          applyServerErrors(mapServerErrors(parsed.fieldErrors, parsed.nonFieldErrors, { dbStorage: values.dbServer.storage }))
        }
        else if (parsed.kind === 'conflict') setSubmitError(`${parsed.message} Check All Deployments before submitting again.`)
        else setSubmitError(parsed.message)
        void queryClient.invalidateQueries({ queryKey: [...workwiseKeys.all, 'shared-servers'] })
        throw error
      }
    } finally {
      setCreating(false)
    }
  }, [form, estimatedMonthlyCost, applyServerErrors, queryClient])

  /** R7-A: max users of a size (admin config, else the size-prices answer); null when not set. */
  const maxUsersFor = useCallback((sku: WorkwiseSku) => maxUsersOf(sku, adminSizes, sizePrices), [adminSizes, sizePrices])

  return {
    enabled,
    form,
    appSku,
    availability,
    vmNames,
    versions: versions.data,
    versionsLoading: versions.isPending,
    adminSizes,
    sizePrices,
    sizePricesStatus,
    maxUsersFor,
    sizeInfo,
    auto,
    serverErrors,
    submitError,
    creating,
    skipApplication,
    setSkipApplication,
    validateStep,
    validateAll,
    create,
  }
}

export type WorkwiseFlow = ReturnType<typeof useWorkwiseFlow>
